interface PollOptions {
  /**
   * Value describing how we should handle interval durations when polling.
   * If a number is provided, that value will be used as a fixed interval between polls.
   * If an object is provided, it can specify either exponential backoff or ramp up behavior.
   * - Exponential backoff will multiply the interval by a multiplier after each run, starting from `initialInterval` and increasing until we reach `maxInterval`.
   * - Ramp up will decrease the interval by `step` after each failed attempt, starting at a high `startInterval` value and stepping down to `endInterval`.
   *   Ramp up is useful for scenarios where we want to start polling infrequently and ram up to polling more frequently as time goes on,
   *   like if we're polling for status updates for a long-running task and we expect it to get closer to completion as time goes on.
   *
   * @default 1000
   */
  interval?:
    | number
    | {
        type: 'exponentialBackoff';
        /**
         * Initial interval in milliseconds.
         */
        startInterval: number;
        /**
         * Maximum interval in milliseconds.
         */
        maxInterval: number;
        /**
         * Optional number by which to multiply the interval on each retry. Default is 2.
         * @default 2
         */
        multiplier?: number;
      }
    | {
        type: 'rampUp';
        /**
         * Starting interval in milliseconds. This should be higher than the `endInterval`.
         */
        startInterval: number;
        /**
         * Ending interval in milliseconds. This should be lower than the `startInterval`.
         */
        endInterval: number;
        /**
         * Step amount in milliseconds to decrease the interval by on each retry.
         */
        step: number;
      };
  /**
   * Max retry attempts before giving up if the poll callback throws an error. Default is Infinity (no limit).
   * @default Infinity
   */
  maxRetries?: number;
}

/**
 * Polls a function at regular intervals until a condition is met or a timeout occurs.
 * @returns {() => void} Cleanup function that can be called to stop polling.
 */
export const poll = (
  callback: () => Promise<unknown> | unknown,
  { interval = 1000, maxRetries = Infinity }: PollOptions = {},
): (() => void) => {
  let attempt = 0;
  let currentInterval: number;
  if (typeof interval === 'number') {
    currentInterval = interval;
  } else {
    currentInterval = interval.startInterval;
  }

  let exponentialBackoffConfig = null;
  let rampUpConfig = null;
  if (typeof interval === 'object') {
    if (interval.type === 'exponentialBackoff') {
      exponentialBackoffConfig = interval;
      if (interval.startInterval >= interval.maxInterval) {
        throw new Error('exponentialBackoff startInterval must be less than maxInterval');
      }
    } else if (interval.type === 'rampUp') {
      // For ramp up, we invert the logic to use exponential backoff with a negative multiplier
      rampUpConfig = interval;
      if (interval.startInterval <= interval.endInterval) {
        throw new Error('rampUp startInterval must be greater than endInterval');
      }
    }
  }

  let isCancelled = false;
  let timeoutID: ReturnType<typeof setTimeout> | null = null;

  const executePoll = async (): Promise<void> => {
    if (isCancelled) {
      return;
    }

    try {
      await callback();
      attempt = 0; // Reset attempt counter on success
    } catch (error) {
      attempt += 1;
      console.error(
        `Polling function encountered an error on attempt ${attempt}. ${
          maxRetries + 1 - attempt
        } retries left:`,
        error,
      );
      if (attempt > maxRetries) {
        throw new Error('Max retries exceeded for polling function.');
      }
    }

    if (isCancelled) {
      // If cancelled during async callback execution, do not schedule further polls
      return;
    }

    // Schedule the next poll first before adjusting the interval for the next run; because we run the poll callback once right away,
    // we want to make sure that the second run honors the initial configured interval instead of skipping right to an adjusted interval.
    timeoutID = setTimeout(executePoll, currentInterval);

    if (exponentialBackoffConfig) {
      const { multiplier = 2, maxInterval } = exponentialBackoffConfig;
      currentInterval = Math.min(currentInterval * multiplier, maxInterval);
    } else if (rampUpConfig) {
      const { endInterval, step } = rampUpConfig;
      currentInterval = Math.max(currentInterval - step, endInterval);
    }
  };
  executePoll();

  // Return cleanup callback
  return () => {
    isCancelled = true;
    if (timeoutID !== null) {
      clearTimeout(timeoutID);
      timeoutID = null;
    }
  };
};
